import { useState, useEffect, useRef, useMemo } from 'react';
import { Package, Plus, Minus, Trash2, Save, PackageOpen, X, Calculator, Tag, ScanBarcode, Pencil, Clock, History, Image, Search, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import api from '../lib/api';
import { BarcodeScanner } from '../components/BarcodeScanner';
import { capitalizeFirst, playSuccessSound, playErrorSound } from '../lib/utils';

// Format price helper
const formatPrice = (value) => {
    if (value === undefined || value === null) return '0,00';
    return value.toFixed(2).replace('.', ',');
};

export const ReceptiePalet = () => {
    // Active pallet
    const [activePallet, setActivePallet] = useState(null);
    const [palletItems, setPalletItems] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Search state
    const [searchItems, setSearchItems] = useState('');
    
    // New pallet form
    const [showNewPalletForm, setShowNewPalletForm] = useState(false);
    const [purchaseValue, setPurchaseValue] = useState('');
    const [nrFactura, setNrFactura] = useState('');
    const [furnizor, setFurnizor] = useState('');
    const [dataFactura, setDataFactura] = useState('');
    const [scadenta, setScadenta] = useState('');
    
    // Scanner state
    const [showScanner, setShowScanner] = useState(false);
    const [continuousScan, setContinuousScan] = useState(true); // Scanare continuă activată implicit
    
    // Add product form
    const [showAddForm, setShowAddForm] = useState(false);
    const [scannedBarcode, setScannedBarcode] = useState('');
    const [productName, setProductName] = useState('');
    const [productCategory, setProductCategory] = useState('');
    const [productQty, setProductQty] = useState(1);
    const [productPrice, setProductPrice] = useState('');  // Preț achiziție per bucată
    const [existingProduct, setExistingProduct] = useState(null);
    
    // Categories
    const [categories, setCategories] = useState([]);
    const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
    const [settings, setSettings] = useState({ allow_new_categories: true, default_category: '' });
    
    // Manual barcode input
    const [manualBarcode, setManualBarcode] = useState('');
    const manualInputRef = useRef(null);
    
    // Edit item modal
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [editName, setEditName] = useState('');
    const [editCategory, setEditCategory] = useState('');
    const [editQty, setEditQty] = useState(1);
    const [showEditCategoryDropdown, setShowEditCategoryDropdown] = useState(false);
    
    // Activity history (last added items)
    const [activityHistory, setActivityHistory] = useState([]);
    
    // Edit pallet modal
    const [showEditPalletModal, setShowEditPalletModal] = useState(false);
    const [editPalletData, setEditPalletData] = useState({
        purchase_value: '',
        nr_factura: '',
        furnizor: '',
        data_factura: '',
        scadenta: '',
        customer_id: ''
    });

    // Multiple pallets support
    const [allOpenPallets, setAllOpenPallets] = useState([]);
    const [showMoveModal, setShowMoveModal] = useState(false);
    const [movingItem, setMovingItem] = useState(null);
    const [targetPalletId, setTargetPalletId] = useState('');
    const [moveQuantity, setMoveQuantity] = useState(1);

    useEffect(() => {
        // Ensure V2 version is set
        loadActivePallet();
        loadCategories();
        loadSettings();
    }, []);
    
    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (showCategoryDropdown && !e.target.closest('[data-category-dropdown]')) {
                setShowCategoryDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showCategoryDropdown]);

    const loadCategories = async () => {
        try {
            const response = await api.get('/categories');
            setCategories(response.data || []);
        } catch (err) {
            console.error('Error loading categories:', err);
        }
    };
    
    const loadSettings = async () => {
        try {
            const response = await api.get('/settings/v2');
            if (response.data) {
                setSettings(response.data);
            }
        } catch (err) {
            console.error('Error loading settings:', err);
        }
    };

    const loadActivePallet = async () => {
        setLoading(true);
        try {
            // Load all open pallets
            const allOpenRes = await api.get('/pallets/all-open');
            setAllOpenPallets(allOpenRes.data || []);
            
            // Load the primary active pallet
            const response = await api.get('/pallets/active');
            if (response.data) {
                setActivePallet(response.data);
                loadPalletItems(response.data.id);
            } else {
                setActivePallet(null);
                setPalletItems([]);
            }
        } catch (err) {
            console.error('Error loading pallet:', err);
            setActivePallet(null);
        }
        setLoading(false);
    };

    const loadPalletItems = async (palletId) => {
        try {
            const [itemsRes, productsRes, activityRes] = await Promise.all([
                api.get(`/pallets/${palletId}/items`),
                api.get('/products/v2'),
                api.get(`/pallets/${palletId}/recent-activity?limit=20`)
            ]);
            
            // Build product metadata map
            const productMap = new Map();
            (productsRes.data || []).forEach(p => {
                productMap.set(p.barcode, p);
            });
            
            // Get last sold prices for all items in a single batch request
            const itemsData = itemsRes.data || [];
            const barcodes = itemsData.map(item => item.barcode).filter(Boolean);
            
            let lastPricesMap = new Map();
            if (barcodes.length > 0) {
                try {
                    const batchRes = await api.post('/products/last-prices/batch', { barcodes });
                    const pricesData = batchRes.data?.prices || {};
                    console.log('Batch prices received:', pricesData);
                    Object.entries(pricesData).forEach(([barcode, price]) => {
                        lastPricesMap.set(barcode, price);
                    });
                } catch (err) {
                    console.error('Failed to fetch batch prices:', err);
                }
            }
            
            // Merge items with product metadata and last sold price
            const itemsWithMeta = itemsData.map(item => {
                const barcodeUpper = item.barcode?.toUpperCase() || '';
                const lastPrice = lastPricesMap.get(barcodeUpper) || lastPricesMap.get(item.barcode) || 0;
                return {
                    ...item,
                    image_url: productMap.get(item.barcode)?.image_url || null,
                    last_sold_price: lastPrice
                };
            });
            
            setPalletItems(itemsWithMeta);
            
            // Set activity history from DB
            const activityData = (activityRes.data || []).map(item => ({
                id: item.created_at || Date.now(),
                name: item.name,
                barcode: item.barcode,
                quantity: item.quantity,
                timestamp: new Date(item.created_at)
            }));
            setActivityHistory(activityData);
        } catch (err) {
            console.error('Error loading items:', err);
        }
    };

    const openNewPalletForm = () => {
        setPurchaseValue('');
        setNrFactura('');
        setFurnizor('');
        setDataFactura('');
        setScadenta('');
        setShowNewPalletForm(true);
    };

    const createNewPallet = async () => {
        if (!purchaseValue || parseFloat(purchaseValue) <= 0) {
            toast.error('Introdu valoarea de achiziție');
            return;
        }
        
        try {
            const response = await api.post('/pallets', {
                purchase_value: parseFloat(purchaseValue),
                nr_factura: nrFactura,
                furnizor: furnizor,
                data_factura: dataFactura,
                scadenta: scadenta
            });
            setActivePallet(response.data);
            setPalletItems([]);
            setShowNewPalletForm(false);
            playSuccessSound();
            toast.success(`Palet #${response.data.number} creat!`);
        } catch (err) {
            playErrorSound();
            toast.error('Eroare la crearea paletului');
            console.error(err);
        }
    };

    const closePallet = async () => {
        if (!activePallet) {
            toast.error('Nu există palet deschis!');
            return;
        }
        try {
            const response = await api.post(`/pallets/${activePallet.id}/close`);
            playSuccessSound();
            toast.success(`Palet închis! Preț/buc: ${response.data.price_per_item?.toFixed(2) || '0'} RON`);
            setActivePallet(null);
            setPalletItems([]);
            loadActivePallet(); // Reload to get remaining open pallets
        } catch (err) {
            playErrorSound();
            const errorMsg = err.response?.data?.detail || 'Eroare la închiderea paletului';
            toast.error(errorMsg);
            console.error('Close pallet error:', err);
        }
    };

    // Create secondary pallet without closing current one
    const createSecondaryPallet = async () => {
        try {
            const response = await api.post('/pallets/open-secondary', {
                purchase_value: 0,
                nr_factura: '',
                furnizor: '',
                data_factura: '',
                scadenta: ''
            });
            playSuccessSound();
            toast.success(`Palet secundar #${response.data.number} creat!`);
            loadActivePallet(); // Reload all pallets
        } catch (err) {
            playErrorSound();
            toast.error('Eroare la crearea paletului secundar');
            console.error(err);
        }
    };

    // Switch to different pallet
    const switchToPallet = async (palletId) => {
        const pallet = allOpenPallets.find(p => p.id === palletId);
        if (pallet) {
            setActivePallet(pallet);
            loadPalletItems(pallet.id);
            toast.success(`Selectat Palet #${pallet.number}`);
        }
    };

    // Open move modal
    const openMoveModal = (item) => {
        setMovingItem(item);
        setMoveQuantity(1);
        setTargetPalletId('');
        setShowMoveModal(true);
    };

    // Move items between pallets
    const handleMoveItems = async () => {
        if (!movingItem || !targetPalletId || moveQuantity <= 0) {
            toast.error('Selectează paletul destinație și cantitatea');
            return;
        }
        
        // Save scroll position before refresh
        const scrollPosition = window.scrollY;
        
        try {
            const response = await api.post('/pallets/move-items', {
                source_pallet_id: activePallet.id,
                target_pallet_id: targetPalletId,
                barcode: movingItem.barcode,
                quantity: parseInt(moveQuantity)
            });
            playSuccessSound();
            toast.success(response.data.message);
            setShowMoveModal(false);
            
            // Reload items without full page refresh
            await loadPalletItems(activePallet.id);
            
            // Restore scroll position after data loads
            setTimeout(() => {
                window.scrollTo(0, scrollPosition);
            }, 100);
        } catch (err) {
            playErrorSound();
            toast.error(err.response?.data?.detail || 'Eroare la mutare');
        }
    };

    // Open edit pallet modal
    const openEditPalletModal = () => {
        if (!activePallet) return;
        setEditPalletData({
            purchase_value: activePallet.purchase_value || '',
            nr_factura: activePallet.nr_factura || '',
            furnizor: activePallet.furnizor || '',
            data_factura: activePallet.data_factura || '',
            scadenta: activePallet.scadenta || '',
            customer_id: activePallet.customer_id || ''
        });
        setShowEditPalletModal(true);
    };

    // Save pallet edits
    const saveEditPallet = async () => {
        if (!activePallet) return;
        try {
            await api.put(`/pallets/${activePallet.id}/edit`, editPalletData);
            toast.success('Date palet actualizate');
            setShowEditPalletModal(false);
            loadActivePallet(); // Reload to get updated data
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare la actualizare');
            console.error(err);
        }
    };

    // Handle barcode scan (from camera or manual)
    const handleBarcodeScan = async (barcode) => {
        setScannedBarcode(barcode);
        // NU închide scanerul - rămâne activ pentru scanare continuă
        
        // Fast combined lookup - single request
        try {
            const response = await api.get(`/products/lookup/${barcode}`);
            if (response.data?.found) {
                setExistingProduct({ 
                    ...response.data.product, 
                    stock_quantity: response.data.stock_quantity 
                });
                setProductName(response.data.product.name);
                setProductCategory(response.data.product.category || '');
            } else {
                setExistingProduct(null);
                setProductName('');
                setProductCategory(settings.default_category || '');
            }
        } catch (err) {
            setExistingProduct(null);
            setProductName('');
            setProductCategory(settings.default_category || '');
        }
        
        setProductQty(1);
        setShowAddForm(true);
    };

    const handleManualSubmit = (e) => {
        e.preventDefault();
        if (manualBarcode.trim()) {
            handleBarcodeScan(manualBarcode.trim());
            setManualBarcode('');
        }
    };

    const handleAddProduct = async (overrideCategory = null) => {
        if (!scannedBarcode) {
            playErrorSound();
            toast.error('Codul de bare lipsește');
            return;
        }
        
        if (!productName.trim()) {
            playErrorSound();
            toast.error('Denumirea produsului este obligatorie');
            return;
        }
        
        const finalCategory = overrideCategory || productCategory;
        
        if (!finalCategory.trim()) {
            playErrorSound();
            toast.error('Categoria este obligatorie');
            return;
        }
        
        if (!activePallet) {
            playErrorSound();
            toast.error('Deschide un palet mai întâi');
            return;
        }

        try {
            const response = await api.post('/pallets/add-item', {
                pallet_id: activePallet.id,
                barcode: scannedBarcode,
                name: productName.trim(),
                category: finalCategory.trim(),
                quantity: productQty,
                purchase_price: productPrice ? parseFloat(productPrice) : null
            });
            
            // Add category to list if new
            if (finalCategory && !categories.includes(finalCategory)) {
                setCategories(prev => [...prev, finalCategory]);
            }
            
            // Add to activity history
            setActivityHistory(prev => [{
                id: Date.now(),
                name: response.data.final_name || productName,
                barcode: scannedBarcode,
                quantity: productQty,
                category: finalCategory,
                price: productPrice,
                timestamp: new Date()
            }, ...prev].slice(0, 20)); // Keep last 20 items
            
            playSuccessSound();
            toast.success(response.data.message || `${productName} x${productQty} adăugat!`);
            setShowAddForm(false);
            setScannedBarcode('');
            setProductName('');
            setProductQty(1);
            setProductPrice('');
            setExistingProduct(null);
            loadPalletItems(activePallet.id);
            
            // Scanerul rămâne deschis - nu facem nimic, e deja activ
        } catch (err) {
            playErrorSound();
            toast.error('Eroare la adăugare');
            console.error(err);
        }
    };

    const updateItemQty = async (itemId, newQty) => {
        if (newQty < 1) return;
        try {
            await api.put(`/pallets/items/${itemId}`, { quantity: newQty });
            setPalletItems(items => 
                items.map(item => item.id === itemId ? { ...item, quantity: newQty } : item)
            );
        } catch (err) {
            toast.error('Eroare la actualizare');
        }
    };

    const removeItem = async (itemId) => {
        try {
            await api.delete(`/pallets/items/${itemId}`);
            setPalletItems(items => items.filter(item => item.id !== itemId));
            toast.success('Produs șters');
        } catch (err) {
            toast.error('Eroare la ștergere');
        }
    };
    
    const openEditModal = (item) => {
        setEditingItem(item);
        setEditName(item.name);
        setEditCategory(item.category || '');
        setEditQty(item.quantity);
        setShowEditModal(true);
    };
    
    const handleSaveEdit = async (overrideCategory = null) => {
        if (!editName.trim()) {
            toast.error('Denumirea este obligatorie');
            return;
        }
        const finalCategory = overrideCategory || editCategory;
        if (!finalCategory.trim()) {
            toast.error('Categoria este obligatorie');
            return;
        }
        try {
            await api.put(`/pallets/items/${editingItem.id}`, {
                name: editName.trim(),
                category: finalCategory.trim(),
                quantity: editQty
            });
            setPalletItems(items =>
                items.map(item => 
                    item.id === editingItem.id 
                        ? { ...item, name: editName.trim(), category: finalCategory.trim(), quantity: editQty }
                        : item
                )
            );
            // Add category to list if new
            if (finalCategory && !categories.includes(finalCategory)) {
                setCategories(prev => [...prev, finalCategory]);
            }
            toast.success('Produs actualizat!');
            setShowEditModal(false);
        } catch (err) {
            toast.error('Eroare la actualizare');
            console.error(err);
        }
    };

    // Calculations
    const totalItems = palletItems.reduce((sum, item) => sum + item.quantity, 0);
    const purchaseVal = activePallet?.purchase_value || 0;
    const estimatedPricePerItem = totalItems > 0 ? purchaseVal / totalItems : 0;
    const totalSellValue = palletItems.reduce((sum, item) => {
        const price = item.last_sold_price || 0;
        return sum + (price * item.quantity);
    }, 0);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin w-8 h-8 border-4 border-violet-500 border-t-transparent rounded-full"></div>
            </div>
        );
    }

    return (
        <div className="pb-24">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-3xl font-bold text-zinc-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Recepție Paleți
                    </h1>
                    <p className="text-zinc-500 mt-1">Scanează și adaugă produse</p>
                </div>
            </div>

            {/* Active Pallet Card */}
            {activePallet ? (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-amber-100 rounded-lg">
                                <PackageOpen size={24} className="text-amber-600" />
                            </div>
                            <div>
                                <h2 className="font-bold text-amber-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                    Palet #{activePallet.number}
                                </h2>
                                <p className="text-sm text-amber-600">
                                    {totalItems} bucăți recepționate
                                </p>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <Button
                                onClick={createSecondaryPallet}
                                variant="outline"
                                className="border-green-300 text-green-700 hover:bg-green-100"
                                title="Deschide palet secundar"
                            >
                                <Plus size={16} className="mr-1" />
                                Palet +
                            </Button>
                            <Button
                                onClick={openEditPalletModal}
                                variant="outline"
                                className="border-blue-300 text-blue-700 hover:bg-blue-100"
                            >
                                <Pencil size={16} className="mr-1" />
                                Editează
                            </Button>
                            <Button
                                onClick={closePallet}
                                variant="outline"
                                className="border-amber-300 text-amber-700 hover:bg-amber-100"
                            >
                                Închide
                            </Button>
                        </div>
                    </div>

                    {/* Multiple Pallets Selector */}
                    {allOpenPallets.length > 1 && (
                        <div className="mb-3 p-2 bg-white rounded-lg border border-amber-200">
                            <p className="text-xs text-zinc-500 mb-2">Palete deschise ({allOpenPallets.length}):</p>
                            <div className="flex flex-wrap gap-2">
                                {allOpenPallets.map(pallet => (
                                    <button
                                        key={pallet.id}
                                        onClick={() => switchToPallet(pallet.id)}
                                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                                            pallet.id === activePallet?.id
                                                ? 'bg-amber-500 text-white'
                                                : 'bg-zinc-100 text-zinc-700 hover:bg-amber-100'
                                        }`}
                                    >
                                        #{pallet.number} ({pallet.total_quantity || 0} buc)
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                    
                    {/* Price Info */}
                    <div className="grid grid-cols-4 gap-3 p-3 bg-white rounded-lg border border-amber-200">
                        <div className="text-center">
                            <p className="text-xs text-zinc-500 mb-1">Valoare Achiziție</p>
                            <p className="text-xl font-bold text-red-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                {formatPrice(purchaseVal)} RON
                            </p>
                        </div>
                        <div className="text-center border-l border-amber-200">
                            <p className="text-xs text-zinc-500 mb-1">Total Bucăți</p>
                            <p className="text-xl font-bold text-amber-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                {totalItems} buc
                            </p>
                        </div>
                        <div className="text-center border-l border-amber-200">
                            <p className="text-xs text-zinc-500 mb-1">Preț Estimat/Buc</p>
                            <p className="text-xl font-bold text-blue-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                {formatPrice(estimatedPricePerItem)} RON
                            </p>
                        </div>
                        <div className="text-center border-l border-amber-200">
                            <p className="text-xs text-zinc-500 mb-1">Val. Vândută</p>
                            <p className="text-xl font-bold text-green-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                {formatPrice(totalSellValue)} RON
                            </p>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="bg-zinc-100 border border-zinc-200 rounded-xl p-6 mb-6 text-center">
                    <PackageOpen size={48} className="mx-auto text-zinc-400 mb-3" />
                    <h2 className="font-bold text-zinc-700 mb-2">Niciun palet deschis</h2>
                    <p className="text-zinc-500 mb-4">Deschide un palet pentru a începe recepția</p>
                    <Button onClick={openNewPalletForm} className="bg-violet-500 hover:bg-violet-600">
                        <Plus size={20} className="mr-2" />
                        Deschide Palet Nou
                    </Button>
                </div>
            )}

            {/* Scan Section - Manual input + Camera button */}
            {activePallet && (
                <div className="mb-6">
                    <form onSubmit={handleManualSubmit} className="flex gap-2">
                        <div className="flex-1 relative">
                            <Input
                                ref={manualInputRef}
                                value={manualBarcode}
                                onChange={(e) => setManualBarcode(e.target.value)}
                                placeholder="Scanează sau introdu cod de bare..."
                                className="h-14 text-lg pr-12"
                                autoFocus
                            />
                            {manualBarcode && (
                                <button 
                                    type="button"
                                    onClick={() => setManualBarcode('')}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                                >
                                    <X size={18} />
                                </button>
                            )}
                        </div>
                        <Button
                            type="button"
                            onClick={() => setShowScanner(true)}
                            className="h-14 px-4 bg-violet-500 hover:bg-violet-600"
                            title="Scanează cu camera"
                        >
                            <ScanBarcode size={24} />
                        </Button>
                    </form>
                </div>
            )}

            {/* Items List - Table with images */}
            {palletItems.length > 0 && (
                <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
                    <div className="px-4 py-3 bg-zinc-50 border-b border-zinc-200 flex items-center justify-between gap-4">
                        <h3 className="font-semibold text-zinc-700 shrink-0">
                            Produse pe palet ({palletItems.length} tipuri, {totalItems} buc)
                        </h3>
                        {/* Search */}
                        <div className="relative flex-1 max-w-xs">
                            <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400" size={14} />
                            <Input
                                value={searchItems}
                                onChange={(e) => setSearchItems(e.target.value)}
                                placeholder="Caută..."
                                className="h-8 pl-8 pr-8 text-sm"
                            />
                            {searchItems && (
                                <button 
                                    onClick={() => setSearchItems('')}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                                >
                                    <X size={14} />
                                </button>
                            )}
                        </div>
                    </div>
                    
                    {/* Table Header */}
                    <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-zinc-50 border-b border-zinc-200 text-xs font-medium text-zinc-500">
                        <div className="col-span-1">Img</div>
                        <div className="col-span-3">Denumire</div>
                        <div className="col-span-2">Cod de bare</div>
                        <div className="col-span-2">Categorie</div>
                        <div className="col-span-1 text-center">Qty</div>
                        <div className="col-span-1 text-right">Ult. Preț</div>
                        <div className="col-span-2 text-center">Acțiuni</div>
                    </div>
                    
                    {/* Table Body */}
                    <div className="divide-y divide-zinc-100 max-h-[400px] lg:max-h-none overflow-y-auto">
                        {[...palletItems]
                            .reverse()
                            .filter(item => {
                                if (!searchItems) return true;
                                const s = searchItems.toLowerCase();
                                return (
                                    item.name?.toLowerCase().includes(s) ||
                                    item.barcode?.toLowerCase().includes(s) ||
                                    item.category?.toLowerCase().includes(s)
                                );
                            })
                            .map((item) => (
                            <div key={item.id} className="grid grid-cols-12 gap-2 px-4 py-2 items-center hover:bg-zinc-50">
                                {/* Image */}
                                <div className="col-span-1">
                                    {item.image_url ? (
                                        <div className="w-10 h-10 rounded-lg border overflow-hidden bg-white">
                                            <img 
                                                src={item.image_url} 
                                                alt={item.name}
                                                className="w-full h-full object-contain"
                                                onError={(e) => e.target.style.display = 'none'}
                                            />
                                        </div>
                                    ) : (
                                        <div className="w-10 h-10 rounded-lg border-2 border-dashed border-zinc-300 flex items-center justify-center bg-zinc-50">
                                            <Image size={14} className="text-zinc-400" />
                                        </div>
                                    )}
                                </div>
                                
                                {/* Name */}
                                <div className="col-span-3">
                                    <span className="font-medium text-zinc-900 text-sm truncate block">{item.name}</span>
                                </div>
                                
                                {/* Barcode */}
                                <div className="col-span-2">
                                    <code className="text-xs bg-zinc-100 px-2 py-1 rounded font-mono">
                                        {item.barcode}
                                    </code>
                                </div>
                                
                                {/* Category */}
                                <div className="col-span-2">
                                    {item.category && (
                                        <span className="text-xs bg-violet-50 text-violet-600 px-2 py-0.5 rounded">
                                            {item.category}
                                        </span>
                                    )}
                                </div>
                                
                                {/* Quantity */}
                                <div className="col-span-1 text-center">
                                    <span className="font-bold text-zinc-900">{item.quantity}</span>
                                </div>
                                
                                {/* Price - Last Sold Price */}
                                <div className="col-span-1 text-right">
                                    <span className={`text-sm font-medium ${item.last_sold_price > 0 ? 'text-green-600' : 'text-zinc-400'}`}>
                                        {item.last_sold_price > 0 ? `${item.last_sold_price.toFixed(2)}` : '-'}
                                    </span>
                                </div>
                                
                                {/* Actions */}
                                <div className="col-span-2 flex justify-center gap-1">
                                    {allOpenPallets.length > 1 && (
                                        <button
                                            onClick={() => openMoveModal(item)}
                                            className="w-7 h-7 flex items-center justify-center text-green-500 hover:bg-green-50 rounded-lg"
                                            title="Mută pe alt palet"
                                        >
                                            <ArrowRight size={14} />
                                        </button>
                                    )}
                                    <button
                                        onClick={() => openEditModal(item)}
                                        className="w-7 h-7 flex items-center justify-center text-blue-500 hover:bg-blue-50 rounded-lg"
                                        title="Editează"
                                    >
                                        <Pencil size={14} />
                                    </button>
                                    <button
                                        onClick={() => removeItem(item.id)}
                                        className="w-7 h-7 flex items-center justify-center text-red-500 hover:bg-red-50 rounded-lg"
                                        title="Șterge"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Activity History - Last added items */}
            {activePallet && activityHistory.length > 0 && (
                <div className="mt-4 bg-white border border-zinc-200 rounded-xl overflow-hidden">
                    <div className="px-4 py-2 bg-zinc-50 border-b border-zinc-200 flex items-center gap-2">
                        <History size={16} className="text-zinc-500" />
                        <h3 className="font-medium text-sm text-zinc-600">Ultimele adăugări</h3>
                    </div>
                    <div className="max-h-[160px] overflow-y-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-zinc-50 text-xs text-zinc-500 sticky top-0">
                                <tr>
                                    <th className="px-3 py-1.5 text-left font-medium">Cod</th>
                                    <th className="px-3 py-1.5 text-left font-medium">Denumire</th>
                                    <th className="px-3 py-1.5 text-center font-medium">Qty</th>
                                    <th className="px-3 py-1.5 text-right font-medium">Data/Ora</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-zinc-100">
                                {activityHistory.map((item) => (
                                    <tr key={item.id} className="hover:bg-zinc-50">
                                        <td className="px-3 py-1.5">
                                            <code className="text-xs bg-zinc-100 px-1.5 py-0.5 rounded font-mono">
                                                {item.barcode?.length > 12 ? item.barcode.slice(-8) : item.barcode}
                                            </code>
                                        </td>
                                        <td className="px-3 py-1.5 text-zinc-700 truncate max-w-[150px]">
                                            {item.name}
                                        </td>
                                        <td className="px-3 py-1.5 text-center font-semibold text-emerald-600">
                                            +{item.quantity}
                                        </td>
                                        <td className="px-3 py-1.5 text-right text-zinc-400 text-xs">
                                            {item.timestamp.toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* New Pallet Modal */}
            <Dialog open={showNewPalletForm} onOpenChange={setShowNewPalletForm}>
                <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                            <PackageOpen className="inline mr-2" size={20} />
                            Palet Nou
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="mt-4 space-y-4">
                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                            <p className="text-sm text-amber-800">
                                Completează datele facturii pentru noul palet
                            </p>
                        </div>
                        
                        <div>
                            <Label>Valoare Achiziție (RON) *</Label>
                            <Input
                                type="number"
                                step="0.01"
                                value={purchaseValue}
                                onChange={(e) => setPurchaseValue(e.target.value)}
                                placeholder="Ex: 500.00"
                                className="mt-1 text-xl font-bold"
                                autoFocus
                            />
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Nr. Factură</Label>
                                <Input
                                    value={nrFactura}
                                    onChange={(e) => setNrFactura(e.target.value)}
                                    placeholder="Ex: FA-1234"
                                    className="mt-1"
                                />
                            </div>
                            <div>
                                <Label>Furnizor</Label>
                                <Input
                                    value={furnizor}
                                    onChange={(e) => setFurnizor(e.target.value)}
                                    placeholder="Ex: SC Furnizor SRL"
                                    className="mt-1"
                                />
                            </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Data Facturii</Label>
                                <Input
                                    type="date"
                                    value={dataFactura}
                                    onChange={(e) => setDataFactura(e.target.value)}
                                    className="mt-1"
                                />
                            </div>
                            <div>
                                <Label>Scadența</Label>
                                <Input
                                    type="date"
                                    value={scadenta}
                                    onChange={(e) => setScadenta(e.target.value)}
                                    className="mt-1"
                                />
                            </div>
                        </div>
                        
                        <div className="flex gap-3 pt-4">
                            <Button
                                variant="outline"
                                onClick={() => setShowNewPalletForm(false)}
                                className="flex-1"
                            >
                                Renunță
                            </Button>
                            <Button
                                onClick={createNewPallet}
                                className="flex-1 bg-amber-500 hover:bg-amber-600"
                            >
                                <PackageOpen size={18} className="mr-2" />
                                Deschide Palet
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Scanner Modal */}
            {/* Barcode Scanner - auto-detects device (camera on mobile, input on PC) */}
            <BarcodeScanner
                isOpen={showScanner}
                onScan={(code) => { 
                    // Nu închide scanerul - handleBarcodeScan va gestiona asta bazat pe continuousScan
                    handleBarcodeScan(code); 
                }}
                onClose={() => setShowScanner(false)}
                keepOpen={continuousScan}
                title={continuousScan ? "Scanare Continuă - Scanează produse" : "Scanează Produs"}
                lowZIndex={showAddForm}
            />

            {/* Add Product Form - appears as bottom panel when scanner is open ON MOBILE ONLY */}
            {showAddForm && showScanner && /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) && (
                <div className="fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-2xl p-4 max-h-[60vh] overflow-y-auto" style={{ zIndex: 150 }}>
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="font-bold text-lg flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            <Package size={20} className="text-violet-600" />
                            {existingProduct ? 'Produs Existent' : 'Produs Nou'}
                        </h3>
                        <button 
                            onClick={() => setShowAddForm(false)}
                            className="p-1 hover:bg-zinc-100 rounded"
                        >
                            <X size={20} />
                        </button>
                    </div>
                    
                    <div className="space-y-3">
                        {/* Stock info for existing product */}
                        {existingProduct && (
                            <div className="px-2 py-1 bg-blue-50 border border-blue-200 rounded text-sm text-blue-700">
                                <strong>În stoc:</strong> {existingProduct.stock_quantity || 0} buc
                            </div>
                        )}
                        
                        {/* Barcode - compact */}
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-zinc-500">Cod:</span>
                            <span className="font-mono text-sm bg-zinc-100 px-2 py-1 rounded">{scannedBarcode}</span>
                        </div>
                        
                        {/* Product Name */}
                        <div>
                            <Label className="text-sm">Denumire *</Label>
                            <Input
                                value={productName}
                                onChange={(e) => setProductName(capitalizeFirst(e.target.value))}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        // Dacă produsul există sau avem toate datele, adaugă
                                        if (existingProduct || (productName && productCategory)) {
                                            handleAddProduct();
                                        }
                                    }
                                }}
                                placeholder="Ex: Set pahare"
                                className="mt-1 h-10"
                                disabled={existingProduct}
                                autoFocus={!existingProduct}
                            />
                        </div>
                        
                        {/* Category with autocomplete - show for new products OR existing without category */}
                        {(!existingProduct || !existingProduct.category || existingProduct.category === 'Necategorizat') && (
                            <div data-category-dropdown className="relative">
                                <Label className="text-sm">Categorie *</Label>
                                <Input
                                    value={productCategory}
                                    onChange={(e) => {
                                        setProductCategory(e.target.value);
                                        setShowCategoryDropdown(true);
                                    }}
                                    onFocus={() => setShowCategoryDropdown(true)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            
                                            // Verifică dacă există o singură predicție
                                            const filtered = categories.filter(c => 
                                                c.toLowerCase().includes(productCategory.toLowerCase())
                                            );
                                            
                                            let finalCategory = productCategory;
                                            if (filtered.length === 1 && productCategory) {
                                                finalCategory = filtered[0];
                                                setProductCategory(filtered[0]);
                                            }
                                            
                                            setShowCategoryDropdown(false);
                                            
                                            if (productName && finalCategory) {
                                                handleAddProduct(finalCategory);
                                            }
                                        }
                                    }}
                                    placeholder="Selectează categorie"
                                    className="mt-1 h-10"
                                />
                                {showCategoryDropdown && (
                                    (() => {
                                        const filtered = categories
                                            .filter(c => c.toLowerCase().includes(productCategory.toLowerCase()))
                                            .sort((a, b) => a.localeCompare(b, 'ro'));
                                        if (filtered.length === 0 && !settings.allow_new_categories) return null;
                                        return (
                                            <div className="absolute z-50 w-full mt-1 bg-white border border-zinc-200 rounded-lg shadow-lg max-h-32 overflow-y-auto">
                                                {filtered.slice(0, 5).map((cat) => (
                                                    <button
                                                        key={cat}
                                                        type="button"
                                                        className="w-full px-3 py-2 text-left hover:bg-zinc-100 text-sm"
                                                        onClick={() => {
                                                            setProductCategory(cat);
                                                            setShowCategoryDropdown(false);
                                                        }}
                                                    >
                                                        {cat}
                                                    </button>
                                                ))}
                                            </div>
                                        );
                                    })()
                                )}
                            </div>
                        )}
                        
                        {/* Quantity selector - compact */}
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-zinc-500">Qty:</span>
                            <div className="flex items-center gap-1">
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setProductQty(Math.max(1, productQty - 1))}
                                    className="w-8 h-8 p-0"
                                >
                                    <Minus size={14} />
                                </Button>
                                <Input
                                    type="number"
                                    value={productQty}
                                    onChange={(e) => setProductQty(Math.max(1, parseInt(e.target.value) || 1))}
                                    onFocus={(e) => e.target.select()}
                                    autoFocus={!!existingProduct}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            if (productName && (existingProduct || productCategory)) {
                                                handleAddProduct();
                                            }
                                        }
                                    }}
                                    className="w-16 text-center font-bold h-8"
                                />
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setProductQty(productQty + 1)}
                                    className="w-8 h-8 p-0"
                                >
                                    <Plus size={14} />
                                </Button>
                            </div>
                        </div>
                        
                        {/* Price input */}
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-zinc-500">Preț:</span>
                            <Input
                                type="number"
                                step="0.01"
                                value={productPrice}
                                onChange={(e) => setProductPrice(e.target.value)}
                                onFocus={(e) => e.target.select()}
                                placeholder="RON/buc"
                                className="w-24 h-8 text-sm"
                            />
                        </div>
                        
                        {/* Quick Add Button */}
                        <Button
                            type="button"
                            onClick={() => handleAddProduct()}
                            disabled={!productName || (!existingProduct && !productCategory)}
                            className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-lg font-bold"
                        >
                            <Plus size={20} className="mr-2" />
                            Adaugă {productQty} buc
                        </Button>
                    </div>
                </div>
            )}

            {/* Add Product Form Modal - for PC or when scanner is closed */}
            <Dialog open={showAddForm && (!showScanner || !/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent))} onOpenChange={setShowAddForm}>
                <DialogContent 
                    className="sm:max-w-md"
                    onKeyDown={(e) => {
                        // Enter oriunde în dialog validează când produsul există
                        if (e.key === 'Enter' && existingProduct && productName) {
                            e.preventDefault();
                            handleAddProduct();
                        }
                    }}
                >
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                            <Package className="inline mr-2" size={20} />
                            {existingProduct ? 'Produs Existent' : 'Produs Nou'}
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="mt-4 space-y-4">
                        {/* Stock info for existing product */}
                        {existingProduct && (
                            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                <p className="text-sm text-blue-700">
                                    <strong>În stoc magazin:</strong> {existingProduct.stock_quantity || 0} buc
                                </p>
                            </div>
                        )}
                        
                        {/* Barcode */}
                        <div>
                            <Label>Cod de Bare</Label>
                            <Input
                                value={scannedBarcode}
                                disabled
                                className="mt-1 font-mono bg-zinc-100"
                            />
                        </div>
                        
                        {/* Product Name */}
                        <div>
                            <Label>Denumire Produs *</Label>
                            <Input
                                value={productName}
                                onChange={(e) => setProductName(capitalizeFirst(e.target.value))}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        // Dacă avem toate datele necesare, adaugă direct
                                        if (productName && (existingProduct || productCategory)) {
                                            handleAddProduct();
                                        }
                                    }
                                }}
                                placeholder="Ex: Set pahare cristal"
                                className="mt-1"
                                disabled={existingProduct}
                                autoFocus={!existingProduct}
                            />
                        </div>
                        
                        {/* Category with autocomplete - allow editing for products without category */}
                        <div data-category-dropdown className="relative">
                            <Label>Categorie *</Label>
                            <Input
                                value={productCategory}
                                onChange={(e) => {
                                    setProductCategory(e.target.value);
                                    // Show dropdown for new products OR existing without category
                                    if (!existingProduct || !existingProduct.category || existingProduct.category === 'Necategorizat') {
                                        setShowCategoryDropdown(true);
                                    }
                                }}
                                onFocus={() => {
                                    if (!existingProduct || !existingProduct.category || existingProduct.category === 'Necategorizat') {
                                        setShowCategoryDropdown(true);
                                    }
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        
                                        // Verifică dacă există o singură predicție
                                        const filtered = categories.filter(c => 
                                            c.toLowerCase().includes(productCategory.toLowerCase())
                                        );
                                        
                                        let finalCategory = productCategory;
                                        if (filtered.length === 1 && productCategory) {
                                            // Selectează automat singura predicție
                                            finalCategory = filtered[0];
                                            setProductCategory(filtered[0]);
                                        }
                                        
                                        setShowCategoryDropdown(false);
                                        
                                        // Dacă avem denumire și categorie, adaugă direct
                                        if (productName && finalCategory) {
                                            handleAddProduct(finalCategory);
                                        }
                                    }
                                }}
                                placeholder="Selectează categorie"
                                className="mt-1"
                                disabled={existingProduct && existingProduct.category && existingProduct.category !== 'Necategorizat'}
                            />
                            {showCategoryDropdown && (!existingProduct || !existingProduct.category || existingProduct.category === 'Necategorizat') && (
                                (() => {
                                    const filtered = categories
                                        .filter(c => c.toLowerCase().includes(productCategory.toLowerCase()))
                                        .sort((a, b) => a.localeCompare(b, 'ro')); // Sortare alfabetică
                                    // Show dropdown if there are matches or if typing new category
                                    if (filtered.length === 0 && !settings.allow_new_categories) return null;
                                    return (
                                        <div className="absolute z-50 w-full mt-1 bg-white border border-zinc-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                            {filtered.map((cat) => (
                                                <button
                                                    key={cat}
                                                    type="button"
                                                    className="w-full px-3 py-2 text-left hover:bg-zinc-100 text-sm flex items-center gap-2"
                                                    onClick={() => {
                                                        setProductCategory(cat);
                                                        setShowCategoryDropdown(false);
                                                    }}
                                                >
                                                    <Tag size={14} className="text-zinc-400" />
                                                    {cat}
                                                </button>
                                            ))}
                                            {productCategory && !filtered.includes(productCategory) && settings.allow_new_categories && (
                                                <button
                                                    type="button"
                                                    className="w-full px-3 py-2 text-left hover:bg-violet-50 text-sm flex items-center gap-2 text-violet-600 border-t"
                                                    onClick={() => setShowCategoryDropdown(false)}
                                                >
                                                    <Plus size={14} />
                                                    Creează "{productCategory}"
                                                </button>
                                            )}
                                        </div>
                                    );
                                })()
                            )}
                        </div>
                        
                        {/* Quantity */}
                        <div>
                            <Label>Cantitate</Label>
                            <div className="flex items-center gap-3 mt-1">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => setProductQty(Math.max(1, productQty - 1))}
                                    className="w-12 h-12"
                                >
                                    <Minus size={20} />
                                </Button>
                                <Input
                                    type="number"
                                    value={productQty}
                                    onChange={(e) => setProductQty(Math.max(1, parseInt(e.target.value) || 1))}
                                    onFocus={(e) => e.target.select()}
                                    autoFocus={!!existingProduct}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            if (productName && (existingProduct || productCategory)) {
                                                handleAddProduct();
                                            }
                                        }
                                    }}
                                    className="w-24 text-center text-2xl font-bold h-12"
                                />
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => setProductQty(productQty + 1)}
                                    className="w-12 h-12"
                                >
                                    <Plus size={20} />
                                </Button>
                            </div>
                        </div>
                        
                        {/* Purchase Price input */}
                        <div>
                            <Label>Preț Achiziție (RON/bucată)</Label>
                            <Input
                                type="number"
                                step="0.01"
                                value={productPrice}
                                onChange={(e) => setProductPrice(e.target.value)}
                                onFocus={(e) => e.target.select()}
                                placeholder="Ex: 15.50"
                                className="mt-1 text-lg"
                            />
                        </div>
                        
                        {/* Estimated price info */}
                        {totalItems > 0 && (
                            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                <div className="flex items-center gap-2 text-blue-700">
                                    <Calculator size={16} />
                                    <span className="text-sm">
                                        Preț estimat: <strong>{formatPrice(estimatedPricePerItem)} RON/buc</strong>
                                    </span>
                                </div>
                            </div>
                        )}
                        
                        {/* Actions */}
                        <div className="flex gap-3 pt-4">
                            <Button
                                onClick={() => setShowAddForm(false)}
                                variant="outline"
                                className="flex-1"
                            >
                                Renunță
                            </Button>
                            <Button
                                type="button"
                                onClick={() => handleAddProduct()}
                                disabled={!productName.trim() || (!existingProduct && !productCategory.trim())}
                                className="flex-1 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                data-testid="add-product-btn"
                            >
                                <Save size={18} className="mr-2" />
                                Adaugă
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Edit Item Modal */}
            <Dialog open={showEditModal} onOpenChange={(open) => {
                setShowEditModal(open);
                if (!open) setShowEditCategoryDropdown(false);
            }}>
                <DialogContent 
                    className="sm:max-w-md"
                    onKeyDown={(e) => {
                        // Enter oriunde în dialog validează
                        if (e.key === 'Enter' && editName.trim() && editCategory.trim()) {
                            e.preventDefault();
                            handleSaveEdit();
                        }
                    }}
                >
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                            <Pencil className="inline mr-2" size={20} />
                            Editează Produs
                        </DialogTitle>
                    </DialogHeader>
                    
                    {editingItem && (
                        <div className="mt-4 space-y-4">
                            <div className="p-2 bg-zinc-100 rounded-lg">
                                <p className="text-xs text-zinc-500 font-mono">{editingItem.barcode}</p>
                            </div>
                            
                            <div>
                                <Label>Denumire Produs *</Label>
                                <Input
                                    value={editName}
                                    onChange={(e) => setEditName(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && editName.trim() && editCategory.trim()) {
                                            e.preventDefault();
                                            handleSaveEdit();
                                        }
                                    }}
                                    placeholder="Ex: Set pahare cristal"
                                    className="mt-1"
                                    autoFocus
                                />
                            </div>
                            
                            {/* Category with autocomplete */}
                            <div className="relative">
                                <Label>Categorie *</Label>
                                <Input
                                    value={editCategory}
                                    onChange={(e) => {
                                        setEditCategory(e.target.value);
                                        setShowEditCategoryDropdown(true);
                                    }}
                                    onFocus={() => setShowEditCategoryDropdown(true)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            
                                            // Verifică dacă există o singură predicție
                                            const filtered = categories.filter(c => 
                                                c.toLowerCase().includes(editCategory.toLowerCase())
                                            );
                                            
                                            let finalCategory = editCategory;
                                            if (filtered.length === 1 && editCategory) {
                                                finalCategory = filtered[0];
                                                setEditCategory(filtered[0]);
                                            }
                                            
                                            setShowEditCategoryDropdown(false);
                                            
                                            if (editName.trim() && finalCategory.trim()) {
                                                handleSaveEdit(finalCategory);
                                            }
                                        }
                                    }}
                                    placeholder="Ex: Veselă"
                                    className="mt-1"
                                />
                                {showEditCategoryDropdown && (
                                    (() => {
                                        const filtered = categories
                                            .filter(c => c.toLowerCase().includes(editCategory.toLowerCase()))
                                            .sort((a, b) => a.localeCompare(b, 'ro'));
                                        if (filtered.length === 0 && !settings.allow_new_categories) return null;
                                        return (
                                            <div className="absolute z-50 w-full mt-1 bg-white border border-zinc-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                                {filtered.map((cat) => (
                                                    <button
                                                        key={cat}
                                                        type="button"
                                                        className="w-full px-3 py-2 text-left hover:bg-zinc-100 text-sm flex items-center gap-2"
                                                        onClick={() => {
                                                            setEditCategory(cat);
                                                            setShowEditCategoryDropdown(false);
                                                        }}
                                                    >
                                                        <Tag size={14} className="text-zinc-400" />
                                                        {cat}
                                                    </button>
                                                ))}
                                                {editCategory && !filtered.includes(editCategory) && settings.allow_new_categories && (
                                                    <button
                                                        type="button"
                                                        className="w-full px-3 py-2 text-left hover:bg-violet-50 text-sm flex items-center gap-2 text-violet-600 border-t"
                                                        onClick={() => setShowEditCategoryDropdown(false)}
                                                    >
                                                        <Plus size={14} />
                                                        Creează "{editCategory}"
                                                    </button>
                                                )}
                                            </div>
                                        );
                                    })()
                                )}
                            </div>
                            
                            {/* Quantity */}
                            <div>
                                <Label>Cantitate</Label>
                                <div className="flex items-center gap-3 mt-1">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => setEditQty(Math.max(1, editQty - 1))}
                                        className="w-10 h-10"
                                    >
                                        <Minus size={16} />
                                    </Button>
                                    <Input
                                        type="number"
                                        min={1}
                                        value={editQty}
                                        onChange={(e) => setEditQty(Math.max(1, parseInt(e.target.value) || 1))}
                                        onFocus={(e) => e.target.select()}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && editName.trim() && editCategory.trim()) {
                                                e.preventDefault();
                                                handleSaveEdit();
                                            }
                                        }}
                                        className="w-20 text-center text-xl font-bold"
                                    />
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => setEditQty(editQty + 1)}
                                        className="w-10 h-10"
                                    >
                                        <Plus size={16} />
                                    </Button>
                                </div>
                            </div>
                            
                            <div className="flex gap-3 pt-4">
                                <Button
                                    onClick={() => setShowEditModal(false)}
                                    variant="outline"
                                    className="flex-1"
                                >
                                    Renunță
                                </Button>
                                <Button
                                    onClick={() => handleSaveEdit()}
                                    disabled={!editName.trim() || !editCategory.trim()}
                                    className="flex-1 bg-blue-500 hover:bg-blue-600"
                                >
                                    <Save size={18} className="mr-2" />
                                    Salvează
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Edit Pallet Modal */}
            <Dialog open={showEditPalletModal} onOpenChange={setShowEditPalletModal}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Pencil size={20} />
                            Editează Palet #{activePallet?.number}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                        <div>
                            <Label>Valoare Achiziție (RON)</Label>
                            <Input
                                type="number"
                                step="0.01"
                                value={editPalletData.purchase_value}
                                onChange={(e) => setEditPalletData(prev => ({ ...prev, purchase_value: e.target.value }))}
                                placeholder="0.00"
                                className="mt-1"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Nr. Factură</Label>
                                <Input
                                    value={editPalletData.nr_factura}
                                    onChange={(e) => setEditPalletData(prev => ({ ...prev, nr_factura: e.target.value }))}
                                    placeholder="Ex: FV001234"
                                    className="mt-1"
                                />
                            </div>
                            <div>
                                <Label>Furnizor</Label>
                                <Input
                                    value={editPalletData.furnizor}
                                    onChange={(e) => setEditPalletData(prev => ({ ...prev, furnizor: e.target.value }))}
                                    placeholder="Nume furnizor"
                                    className="mt-1"
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Data Factură</Label>
                                <Input
                                    type="date"
                                    value={editPalletData.data_factura}
                                    onChange={(e) => setEditPalletData(prev => ({ ...prev, data_factura: e.target.value }))}
                                    className="mt-1"
                                />
                            </div>
                            <div>
                                <Label>Scadență</Label>
                                <Input
                                    type="date"
                                    value={editPalletData.scadenta}
                                    onChange={(e) => setEditPalletData(prev => ({ ...prev, scadenta: e.target.value }))}
                                    className="mt-1"
                                />
                            </div>
                        </div>
                        <div>
                            <Label>Alocat Clientului (opțional)</Label>
                            <Input
                                value={editPalletData.customer_id}
                                onChange={(e) => setEditPalletData(prev => ({ ...prev, customer_id: e.target.value }))}
                                placeholder="ID client (lasă gol pentru nealocat)"
                                className="mt-1"
                            />
                            <p className="text-xs text-zinc-500 mt-1">
                                Dacă este alocat unui client, nu se va scădea din comenzi virtuale
                            </p>
                        </div>
                        <div className="flex gap-3 pt-2">
                            <Button
                                onClick={() => setShowEditPalletModal(false)}
                                variant="outline"
                                className="flex-1"
                            >
                                Anulează
                            </Button>
                            <Button
                                onClick={saveEditPallet}
                                className="flex-1 bg-blue-600 hover:bg-blue-700"
                            >
                                Salvează
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Move Items Modal */}
            <Dialog open={showMoveModal} onOpenChange={setShowMoveModal}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            <ArrowRight className="text-green-500" size={20} />
                            Mută pe Alt Palet
                        </DialogTitle>
                    </DialogHeader>
                    
                    {movingItem && (
                        <div className="space-y-4 mt-4">
                            {/* Item info */}
                            <div className="p-3 bg-zinc-100 rounded-lg">
                                <p className="font-medium text-zinc-800">{movingItem.name}</p>
                                <p className="text-sm text-zinc-500">{movingItem.barcode}</p>
                                <p className="text-sm text-zinc-600">Disponibil: <strong>{movingItem.quantity} buc</strong></p>
                            </div>
                            
                            {/* Quantity */}
                            <div>
                                <Label>Cantitate de mutat</Label>
                                <Input
                                    type="number"
                                    min="1"
                                    max={movingItem.quantity}
                                    value={moveQuantity}
                                    onChange={(e) => setMoveQuantity(Math.min(parseInt(e.target.value) || 1, movingItem.quantity))}
                                    className="mt-1"
                                />
                            </div>
                            
                            {/* Target pallet */}
                            <div>
                                <Label>Palet Destinație</Label>
                                <div className="flex flex-wrap gap-2 mt-2">
                                    {allOpenPallets
                                        .filter(p => p.id !== activePallet?.id)
                                        .map(pallet => (
                                            <button
                                                key={pallet.id}
                                                onClick={() => setTargetPalletId(pallet.id)}
                                                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                                    targetPalletId === pallet.id
                                                        ? 'bg-green-500 text-white'
                                                        : 'bg-zinc-100 text-zinc-700 hover:bg-green-100'
                                                }`}
                                            >
                                                Palet #{pallet.number}
                                            </button>
                                        ))
                                    }
                                </div>
                            </div>
                            
                            <div className="flex gap-3 pt-2">
                                <Button
                                    onClick={() => setShowMoveModal(false)}
                                    variant="outline"
                                    className="flex-1"
                                >
                                    Anulează
                                </Button>
                                <Button
                                    onClick={handleMoveItems}
                                    className="flex-1 bg-green-500 hover:bg-green-600"
                                    disabled={!targetPalletId || moveQuantity <= 0}
                                >
                                    <ArrowRight size={16} className="mr-2" />
                                    Mută
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default ReceptiePalet;
